import React, { useState, useRef, useEffect } from "react";
import "./LoginSignUp.css";
import Seo from "../../Seo/Seo";
import { useSiteSeo } from "../../../context/SiteSeoContext";
import { truncateMetaDescription } from "../../../utils/seoText";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { setCustomerToken, customerApi, API_BASE } from "../../../api/client";
import { executeRecaptchaEnterprise } from "../../../utils/recaptchaEnterprise";
import { auth, GoogleAuthProvider } from "../../../firebase";
import {
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from "firebase/auth";

/** Firebase Phone Auth requires E.164 (+country…). Normalize common India 10-digit mobiles to +91. */
function toE164Phone(input) {
  const raw = String(input || "").trim().replace(/[\s-]/g, "");
  if (!raw) return "";
  if (raw.startsWith("+")) return raw;
  // India: 10 digits starting 6–9, optional leading 0
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10 && /^[6-9]/.test(digits)) {
    return `+91${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("0") && /^0[6-9]\d{9}$/.test(digits)) {
    return `+91${digits.slice(1)}`;
  }
  return raw.startsWith("+") ? raw : `+${raw}`;
}

function firebasePhoneErrorMessage(err) {
  const code = err?.code || "";
  const msg = String(err?.message || "");
  if (code === "auth/invalid-phone-number") {
    return "Invalid phone number. Use full number with country code, e.g. +91 98765 43210.";
  }
  if (code === "auth/missing-phone-number") return "Enter a phone number.";
  if (code === "auth/quota-exceeded" || code === "auth/too-many-requests") {
    return "Firebase is temporarily blocking SMS for this number or IP (too-many-requests). Wait at least 30–60 minutes without retrying. For development, use Authentication → Sign-in method → Phone → Phone numbers for testing (fixed SMS codes, no real SMS). Or sign in with Email / Google.";
  }
  if (code === "auth/captcha-check-failed") {
    return "reCAPTCHA failed. Tick the checkbox, wait for it to finish, then tap Send OTP. Turn off ad blockers for this site.";
  }
  if (code === "auth/invalid-app-credential" || code === "auth/missing-app-credential") {
    return "Firebase rejected this app’s credentials. In Google Cloud Console → APIs & Services → Credentials, ensure your browser API key allows Identity Toolkit and is not overly restricted; check Authorized domains in Firebase Authentication.";
  }
  if (code === "auth/unauthorized-domain") {
    return "This domain is not authorized. Add localhost (and your production domain) under Firebase Authentication → Settings → Authorized domains.";
  }
  if (code === "auth/operation-not-allowed") {
    if (/region/i.test(msg)) {
      return "Firebase is blocking SMS to this country until you allow it. Open Firebase Console → Authentication → Settings → SMS region policy (or SMS / Fraud prevention), and allow your region (e.g. India for +91). Then retry.";
    }
    return "Phone sign-in is disabled or blocked. Enable Phone under Authentication → Sign-in method, and allow SMS for your region under Authentication → Settings.";
  }
  return err?.message || "Failed to send code.";
}

const LoginSignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Restrict redirect to same-origin paths only — prevent open redirect phishing
  const rawRedirect = searchParams.get("redirect") || "/account";
  const redirectTo = rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : "/account";
  const siteCtx = useSiteSeo();
  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";
  const [activeTab, setActiveTab] = useState("tabButton1");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [firebaseError, setFirebaseError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneStep, setPhoneStep] = useState("enter");
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerRef = useRef(null);
  /** Cooldown after failed Send OTP; cleared on success so changing number / resend flow is not blocked. */
  const lastOtpAttemptAtRef = useRef(0);
  /** Must tick reCAPTCHA before Send OTP — calling SMS API without a solved widget yields 400 from Identity Toolkit. */
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  /** Bump to remount reCAPTCHA after a failed SMS (token may be consumed). */
  const [recaptchaMountKey, setRecaptchaMountKey] = useState(0);

  /** Visible reCAPTCHA (normal): invisible + off-screen CSS breaks verification on localhost (400 / CAPTCHA_CHECK_FAILED). */
  useEffect(() => {
    if (!auth || phoneStep !== "enter") return;
    const el = recaptchaContainerRef.current;
    if (!el) return;

    setRecaptchaReady(false);
    try {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear?.();
        } catch (_) {}
        recaptchaVerifierRef.current = null;
      }
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, el, {
        size: "normal",
        callback: () => setRecaptchaReady(true),
        "expired-callback": () => setRecaptchaReady(false),
      });
    } catch (e) {
      console.warn("RecaptchaVerifier init:", e);
    }
    return () => {
      setRecaptchaReady(false);
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear?.();
        } catch (_) {}
        recaptchaVerifierRef.current = null;
      }
    };
  }, [auth, phoneStep, recaptchaMountKey]);

  const handleTab = (tab) => {
    setActiveTab(tab);
    setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Email and password required.");
      return;
    }
    setLoading(true);
    let recaptchaToken = null;
    try {
      recaptchaToken = await executeRecaptchaEnterprise("CUSTOMER_LOGIN");
    } catch (recaptchaErr) {
      setError(recaptchaErr.message || "Security check failed to load.");
      setLoading(false);
      return;
    }
    try {
      const data = await customerApi("/api/customer/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password, recaptchaToken }),
      });
      setCustomerToken(data.token);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Name required.");
      return;
    }
    if (!email.trim() || !password) {
      setError("Email and password required.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    let recaptchaToken = null;
    try {
      recaptchaToken = await executeRecaptchaEnterprise("CUSTOMER_REGISTER");
    } catch (recaptchaErr) {
      setError(recaptchaErr.message || "Security check failed to load.");
      setLoading(false);
      return;
    }
    try {
      const data = await customerApi("/api/customer/register", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          password,
          phone: phone.trim() || undefined,
          recaptchaToken,
        }),
      });
      setCustomerToken(data.token);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const exchangeFirebaseToken = async (idToken) => {
    const res = await fetch(API_BASE + "/api/customer/auth/firebase", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || res.statusText || "Sign-in failed");
    return data;
  };

  const handleGoogleSignIn = async () => {
    if (!auth) {
      setFirebaseError("Google sign-in is not configured.");
      return;
    }
    setFirebaseError("");
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      const data = await exchangeFirebaseToken(idToken);
      setCustomerToken(data.token);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") {
        setFirebaseError("Sign-in was cancelled.");
      } else {
        setFirebaseError(err.message || "Google sign-in failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneCode = async (e) => {
    e.preventDefault();
    const number = toE164Phone(phoneNumber);
    if (!number || number === "+") {
      setFirebaseError("Enter a phone number.");
      return;
    }
    if (!auth) {
      setFirebaseError(
        "Phone sign-in is not configured. Add REACT_APP_FIREBASE_* keys and restart the dev server."
      );
      return;
    }
    const OTP_COOLDOWN_MS = 30_000;
    const now = Date.now();
    const sinceLast = now - lastOtpAttemptAtRef.current;
    if (lastOtpAttemptAtRef.current > 0 && sinceLast < OTP_COOLDOWN_MS) {
      const waitSec = Math.ceil((OTP_COOLDOWN_MS - sinceLast) / 1000);
      setFirebaseError(
        `Please wait ${waitSec}s before Send OTP again — Firebase rate-limits rapid retries.`
      );
      return;
    }
    lastOtpAttemptAtRef.current = now;

    setFirebaseError("");
    setLoading(true);
    try {
      const appVerifier = recaptchaVerifierRef.current;
      if (!appVerifier) throw new Error("Verification not ready. Refresh the page.");
      const conf = await signInWithPhoneNumber(auth, number, appVerifier);
      lastOtpAttemptAtRef.current = 0;
      setConfirmationResult(conf);
      setPhoneStep("code");
    } catch (err) {
      if (process.env.NODE_ENV === "development" && err?.code !== "auth/too-many-requests") {
        // eslint-disable-next-line no-console
        console.error("[phone OTP]", err?.code, err?.message, err);
      }
      setFirebaseError(firebasePhoneErrorMessage(err));
      setRecaptchaReady(false);
      setRecaptchaMountKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhoneCode = async (e) => {
    e.preventDefault();
    if (!confirmationResult || !verificationCode.trim()) {
      setFirebaseError("Enter the code we sent.");
      return;
    }
    setFirebaseError("");
    setLoading(true);
    try {
      const result = await confirmationResult.confirm(verificationCode.trim());
      const idToken = await result.user.getIdToken();
      const data = await exchangeFirebaseToken(idToken);
      setCustomerToken(data.token);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setFirebaseError(err.message || "Invalid code.");
    } finally {
      setLoading(false);
    }
  };

  const resetPhoneFlow = () => {
    setPhoneStep("enter");
    setConfirmationResult(null);
    setVerificationCode("");
    setFirebaseError("");
  };

  return (
    <>
      <Seo
        title={`Sign in${titleSuffix}`}
        description={truncateMetaDescription(
          "Sign in or create your Gem Fashion Studios account to book designer clothing rentals."
        )}
        canonicalPath="/loginSignUp"
        noindex
        noindexFollow
      />
      <div className="loginSignUpSection loginSignUpV2">
      <div className="loginSignUpCard">
        {/* Left: promo panel */}
        <div className="loginSignUpLeft">
          <h2 className="loginSignUpLeftTitle">
            Simplify your rental experience.
          </h2>
          <p className="loginSignUpLeftSub">
            Sign in or create an account to browse outfits, manage orders, and enjoy a seamless fashion rental experience.
          </p>
        </div>
        {/* Right: form card */}
        <div className="loginSignUpRight">
          <div className="loginSignUpRightInner">
            <div className="loginSignUpLogo">
              <span className="loginSignUpLogoIcon">🛒</span>
              <span className="loginSignUpLogoText">Gem Fashion</span>
            </div>
            <h1 className="loginSignUpWelcome">
              {activeTab === "tabButton1" ? "Welcome Back." : "Create Account."}
            </h1>
            <p className="loginSignUpSubtext">
              {activeTab === "tabButton1"
                ? "Please login to your account."
                : "Please fill in your details to register."}
            </p>
            {error && <p className="loginSignUpError">{error}</p>}
            {firebaseError && <p className="loginSignUpError">{firebaseError}</p>}

            {activeTab === "tabButton1" ? (
              <form onSubmit={handleLogin} className="loginSignUpForm">
                <input
                  type="email"
                  placeholder="Email address"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="loginSignUpInput"
                />
                <div className="loginSignUpPasswordWrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="loginSignUpInput"
                  />
                  <button
                    type="button"
                    className="loginSignUpEye"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>
                <div className="loginSignUpForgotWrap">
                  <Link to="/resetPassword" className="loginSignUpForgot">
                    Forgot password?
                  </Link>
                </div>
                <button type="submit" className="loginSignUpSubmit" disabled={loading}>
                  {loading ? "Logging in…" : "Login"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="loginSignUpForm">
                <input
                  type="text"
                  placeholder="Name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="loginSignUpInput"
                />
                <input
                  type="email"
                  placeholder="Email address"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="loginSignUpInput"
                />
                <input
                  type="password"
                  placeholder="Password (min 6 characters)"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="loginSignUpInput"
                />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="loginSignUpInput"
                />
                <p className="loginSignUpPrivacy">
                  By signing up you agree to our{" "}
                  <Link to="/privacy-policy" className="loginSignUpLinkInline">privacy policy</Link>.
                </p>
                <button type="submit" className="loginSignUpSubmit" disabled={loading}>
                  {loading ? "Creating account…" : "Register"}
                </button>
              </form>
            )}

            <div className="loginSignUpOrDivider">
              <span className="loginSignUpOrLine" />
              <span className="loginSignUpOrText">Or login with</span>
              <span className="loginSignUpOrLine" />
            </div>
            <div className="loginSignUpSocialRow">
              <button
                type="button"
                className="loginSignUpGoogleBtn"
                onClick={handleGoogleSignIn}
                disabled={loading}
                aria-label="Sign in with Google"
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/google-signin-button.png`}
                  alt="Sign in with Google"
                  className="loginSignUpGoogleBtnImg"
                />
              </button>
              <div className="loginSignUpPhoneInline loginSignUpPhoneColumn">
                {phoneStep === "enter" ? (
                  <>
                    <form onSubmit={handleSendPhoneCode} className="loginSignUpPhoneFormRow">
                      <input
                        type="tel"
                        placeholder="Mobile (+91 or full international)"
                        autoComplete="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="loginSignUpInput loginSignUpInputSmall"
                      />
                      <button type="submit" disabled={loading} className="loginSignUpPhoneBtn">
                        {loading ? "…" : "Send OTP"}
                      </button>
                    </form>
                    <p className="loginSignUpRecaptchaHint">
                      {recaptchaReady
                        ? "reCAPTCHA completed — tap Send OTP."
                        : "Tick “I’m not a robot” below, then tap Send OTP (required by Firebase)."}
                    </p>
                    <div
                      key={recaptchaMountKey}
                      ref={recaptchaContainerRef}
                      id="recaptcha-container"
                      className="loginSignUpRecaptchaMount"
                    />
                  </>
                ) : (
                  <form onSubmit={handleVerifyPhoneCode} className="loginSignUpPhoneFormRow loginSignUpPhoneFormRowWrap">
                    <input
                      type="text"
                      placeholder="Code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      maxLength={6}
                      className="loginSignUpInput loginSignUpInputSmall"
                    />
                    <div className="loginSignUpPhoneActions">
                      <button type="submit" disabled={loading} className="loginSignUpPhoneBtn">
                        Verify
                      </button>
                      <button type="button" className="loginSignUpBackPhone" onClick={resetPhoneFlow} disabled={loading}>
                        Back
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
            <p className="loginSignUpSwitch">
              {activeTab === "tabButton1" ? (
                <>
                  Don&apos;t have an account?{" "}
                  <button type="button" className="loginSignUpLink" onClick={() => handleTab("tabButton2")}>
                    Signup
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button type="button" className="loginSignUpLink" onClick={() => handleTab("tabButton1")}>
                    Login
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default LoginSignUp;
